import "./EntryAddView.less";

import { notification, Button, Empty, Input, Table, Tooltip } from "antd";
import { ColumnProps } from "antd/lib/table";
import { difference, maxBy } from "lodash";
import * as React from "react";

import { EntryApi, OrderedEntryApi, SearchApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps, IHighlightProps } from "lib/data/interfaces";
import { Entry, EntryGroup, OrderedEntry, Section } from "lib/data/model";
import { SearchItem } from "lib/data/transfer";
import { ComponentService, StateService } from "lib/services";

import { GradientBar } from "components/general";
import cloneDeep from "lodash/cloneDeep";


interface IEntryAddViewProps extends IBaseProps, IHighlightProps {
	section: Section;
	entryGroup: EntryGroup;
	onComplete: (entryGroup: EntryGroup) => void;
}

interface IEntryAddViewState {
	search: string;
	searchMessage?: ImportSearchResultMessage;
	selectedEntries: string[];
	isSearching: boolean;
}

enum ImportSearchResultMessage {
	NONE = "No Results Found",
	FILTERED = "All Results Found Are Already In Group"
}


export class EntryAddView extends React.Component<IEntryAddViewProps, IEntryAddViewState> {

	private apiCancellationToken: CancellationToken = new CancellationToken();


	private entryGroup!: EntryGroup;
	private searchResults: Entry[] = [];
	private hierarchies: Map<number, Section[][]> = new Map<number, Section[][]>();

	constructor(props: IEntryAddViewProps) {

		super(props);

		this.state = {
			search: "",
			selectedEntries: [],
			isSearching: false
		};
	}

	public componentWillMount() {

		// Set Entry Group
		this.entryGroup = cloneDeep(this.props.entryGroup);
	}

	public async componentWillReceiveProps(newProps: IEntryAddViewProps) {

		// Set Entry Group
		this.entryGroup = cloneDeep(newProps.entryGroup);
	}

	public componentWillUnmount() {

		// Cancel Outstanding API Calls
		this.apiCancellationToken.cancel();
	}

	public render() {

		// Define Class Name
		const className = [
			"entry-add-view",
			this.props.className
		].filter((c) => c !== undefined).join(" ").trim();

		// Define Columns
		const columns: Array<ColumnProps<Entry>> = [
			{
				dataIndex: "name",
				title: "Name"
			}
		];

		return (

			<div className={className}>

				<div className="entry-search-bar">

					<div className="entry-search">

						<Tooltip title="Import From Selected Records">
							<Button
								onClick={this.import}
								disabled={this.state.selectedEntries.length <= 0}
								type="primary"
							>
								Import
							</Button>
						</Tooltip>

						<Tooltip title="Create New From 'Name'">
							<Button
								onClick={this.create}
								disabled={this.state.search === ""}
								type="primary"
							>
								Create
							</Button>
						</Tooltip>

						<Input
							className="entry-search-input"
							placeholder="Name"
							value={this.state.search}
							onChange={this.onSearchValueChange}
							onKeyPress={this.onSearchKeyPress}
							allowClear={true}
						/>

						<Tooltip title="Search">
							<Button
								icon="search"
								onClick={this.onSearchClicked}
							/>
						</Tooltip>
					</div>

					<Table
						className={className}
						loading={this.state.isSearching}
						rowKey={this.getTableRowKey}
						size="small"
						columns={columns}
						dataSource={this.searchResults}
						rowSelection={{
							selectedRowKeys: this.state.selectedEntries,
							onChange: (selectedEntries) => StateService.setCancelableState(() => this.setState({ selectedEntries: selectedEntries as string[] }), this.apiCancellationToken)
						}}
						expandedRowRender={this.getEntryDetailsView}
						locale={{
							emptyText: (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description={(this.state.searchMessage !== undefined ? this.state.searchMessage : "No Search Performed")}
								/>
							)
						}}
					/>
				</div>

			</div>
		);
	}

	private getEntryDetailsView = (entry: Entry): React.ReactNode => {

		const hierarchies = this.hierarchies.get(entry.id!);

		if (hierarchies !== undefined && hierarchies.length > 0) {

			return hierarchies.filter((i) => i[0].published !== undefined || i[0].draft !== undefined).map((hierarchy) => {

				// Ensure Hierarchy Exists
				if (hierarchy.length > 0) {

					// Clone Hierarchy
					const clonedHierarchy = cloneDeep(hierarchy);

					// Get Immediate Parent
					const immediateParent = clonedHierarchy.pop() as Section;

					// Get Parent's Name
					const parentName = immediateParent.published!.name;

					// Ensure Parent Name Exists
					if (parentName) {

						return (
							<GradientBar
								size="small"
								title={parentName}
								superTitle={ComponentService.getHierarchyString(clonedHierarchy)}
								key={immediateParent.id}
							/>
						);
					}
				}

				// Return Nothing
				return null;
			});
		}

		return "This record is not contained in any published groups.";
	}

	private onSearchValueChange = async (event: React.ChangeEvent<HTMLInputElement>) => {

		// Check For Empty Value
		if (event.target.value === "") {

			// Update Search Term
			StateService.setCancelableState(() => this.setState({ searchMessage: undefined, selectedEntries: [], search: event.target.value }), this.apiCancellationToken);
		}
		else {

			// Update Search Term
			StateService.setCancelableState(() => this.setState({ search: event.target.value }), this.apiCancellationToken);
		}
	}

	private onSearchKeyPress = async (event: React.KeyboardEvent) => {

		// Switch On Key Pressed
		switch (event.key) {

			case "Enter":

				// Perform Search
				await this.search();

				break;
		}
	}

	private onSearchClicked = async () => {

		// Perform Search
		await this.search();
	}

	private getTableRowKey = (record: Entry): string => {

		// Return Record ID (Converted To A String)
		return record.id!.toString();
	}

	private search = async () => {

		// Indicate Searching
		StateService.setCancelableState(() => this.setState({ isSearching: true }), this.apiCancellationToken);

		// Initialize Results
		let results: Entry[];
		this.hierarchies.clear();

		// Check Search Phrase
		if (this.state.search !== "") {

			// Perform Search
			const searchResults: SearchItem[] = await SearchApi.search({ searchPhrase: this.state.search, objects: ["Entry"], properties: ["Name"] });

			// Process Search Results
			results = searchResults.map((result) => result.item as Entry);
		}
		else {

			results = await EntryApi.getEntries();
		}

		// Check Results
		if (results.length > 0) {

			// Get Current Entry IDs
			const currentEntryIds = this.entryGroup.entries.map((currentEntry) => currentEntry.item.id!);

			// Filter Results
			const filteredResults = results.filter((result) => !currentEntryIds.includes(result.id!));

			// Check Filtered Results
			if (filteredResults.length > 0) {

				// Initialize Promises
				const promises: Array<Promise<void>> = [];

				// Loop Through Results
				for (const result of filteredResults) {

					// Add Retrieval Promise To Array
					promises.push(new Promise<void>(async (resolve) => {

						// Get Hierarchies For Result
						const hierarchies = await EntryApi.getHierarchy(result.id);

						// Store Hierarchies
						this.hierarchies.set(result.id!, hierarchies);
						resolve();
					}));
				}

				// Wait For All Hierarchies To Be Retrieved
				await Promise.all(promises);

				// Populate Dataset
				this.searchResults = filteredResults;

				// Check For Filtered Results
				if (filteredResults.length < results.length) {

					// Notify User
					notification.warning({
						message: "Results Filtered",
						description: "Some results were filtered as they are already in the group"
					});

					// Reset Search Message
					StateService.setCancelableState(() => this.setState({ selectedEntries: [], searchMessage: ImportSearchResultMessage.FILTERED, isSearching: false }), this.apiCancellationToken);
				}
				else {

					// Set Search Message
					StateService.setCancelableState(() => this.setState({ selectedEntries: [], searchMessage: undefined, isSearching: false }), this.apiCancellationToken);
				}
			}
			else {

				this.searchResults = [];

				// Notify User
				notification.warning({
					message: "Results Filtered",
					description: "All results were filtered as they are already in the group"
				});

				// Set Search Message
				StateService.setCancelableState(() => this.setState({ selectedEntries: [], searchMessage: ImportSearchResultMessage.FILTERED, isSearching: false }), this.apiCancellationToken);
			}
		}
		else {

			this.searchResults = [];

			// Set Search Message
			StateService.setCancelableState(() => this.setState({ selectedEntries: [], searchMessage: ImportSearchResultMessage.NONE, isSearching: false }), this.apiCancellationToken);
		}
	}

	private import = async () => {

		// Check For Selected Entries
		if (this.state.selectedEntries.length > 0) {

			// Get Selected Entries
			const selectedEntries = this.searchResults.filter((entry) => this.state.selectedEntries.includes(entry.id!.toString()));

			// Initialize Promise Array
			const promises: Array<Promise<void>> = [];

			// Loop Through Imported Entries
			for (const entry of selectedEntries) {

				// Calculate Max Order In Entries
				const highestOrderEntry = maxBy(this.entryGroup.entries, (i) => i.order);

				// Construct Ordered Entry
				const orderedEntry = new OrderedEntry();
				orderedEntry.order = (highestOrderEntry ? highestOrderEntry.order + 1 : 0);
				orderedEntry.item = entry;

				// Create Promise
				promises.push(new Promise<void>(async (resolve) => {

					// Save Ordered Entry
					const savedOrderedEntry = await OrderedEntryApi.save(this.props.section, this.entryGroup, orderedEntry);

					// Check Saved Ordered Entry
					if (savedOrderedEntry !== undefined) {

						// Add Imported Entry To List
						this.entryGroup.entries.push(savedOrderedEntry);
					}

					// Resolve Promise
					resolve();
				}));
			}

			// Await Promises
			await Promise.all(promises);

			// Remove Imports From Search Results
			const filteredResults = difference(this.searchResults, selectedEntries);

			// Populate Dataset
			this.searchResults = filteredResults;

			// Check Newly Filtered Results
			if (filteredResults.length > 0) {

				// Reset Search Message
				StateService.setCancelableState(() => this.setState({ selectedEntries: [], searchMessage: undefined }), this.apiCancellationToken);
			}
			else {

				// Set Search Message
				StateService.setCancelableState(() => this.setState({ selectedEntries: [], searchMessage: ImportSearchResultMessage.FILTERED }), this.apiCancellationToken);
			}

			// Notify User
			notification.success({
				message: "Imported Successfully"
			});

			// Indicate Creation Is Complete
			this.props.onComplete(this.entryGroup);
		}
	}

	private create = async () => {

		// Extract Entry Name
		const name: string = this.state.search;

		// Construct Entry
		const newEntry = new Entry();
		newEntry.name = name;

		// Create Entry
		const createdEntry = await EntryApi.saveEntry(newEntry);

		// Check Created Entry
		if (createdEntry !== undefined) {

			// Calculate Max Order In Entries
			const highestOrderEntry = maxBy(this.entryGroup.entries, (i) => i.order);

			// Construct Ordered Entry
			const orderedEntry = new OrderedEntry();
			orderedEntry.order = (highestOrderEntry ? highestOrderEntry.order + 1 : 0);
			orderedEntry.item = createdEntry;

			// Save Ordered Entry
			const savedOrderedEntry = await OrderedEntryApi.save(this.props.section, this.entryGroup, orderedEntry);

			// Check Saved Ordered Entry
			if (savedOrderedEntry !== undefined) {

				// Add Imported Entry To List
				this.entryGroup.entries.push(savedOrderedEntry);

				// Notify User
				notification.success({
					message: "Created Successfully"
				});

				// Indicate Creation Is Complete
				this.props.onComplete(this.entryGroup);
			}
		}
	}
}
